import { todayYmdInOrgTimeZone } from './organizationalTime';

export const DAILY_WINS_EVENT = 't1-daily-wins-updated';

export const EMPTY_DAILY_WINS = Object.freeze({
  alertsResolved: 0,
  threadsCleared: 0,
  messagesSent: 0,
  patientsActivated: 0,
  careMinutes: 0,
  inboxZeroMoments: 0,
});

export function dailyWinsStorageKey(uid, ymd = todayYmdInOrgTimeZone()) {
  return `t1care.dailyWins:${uid}:${ymd}`;
}

export function emptyDailyWinsRecord(uid, ymd = todayYmdInOrgTimeZone()) {
  return {
    uid,
    ymd,
    counters: { ...EMPTY_DAILY_WINS },
    eventIds: [],
    clearedPatientIds: [],
    activatedPatientIds: [],
  };
}

export function readDailyWins(uid, ymd = todayYmdInOrgTimeZone()) {
  if (!uid) return emptyDailyWinsRecord('', ymd);
  try {
    const parsed = JSON.parse(sessionStorage.getItem(dailyWinsStorageKey(uid, ymd)) || 'null');
    if (parsed?.uid === uid && parsed?.ymd === ymd) {
      return {
        ...emptyDailyWinsRecord(uid, ymd),
        ...parsed,
        counters: { ...EMPTY_DAILY_WINS, ...(parsed.counters || {}) },
      };
    }
  } catch {
    // Storage is optional; return a fresh in-memory-compatible record.
  }
  return emptyDailyWinsRecord(uid, ymd);
}

export function wasDailyWinRecorded(uid, eventId, ymd = todayYmdInOrgTimeZone()) {
  return Boolean(uid && eventId && readDailyWins(uid, ymd).eventIds.includes(eventId));
}

function writeDailyWins(record) {
  try {
    sessionStorage.setItem(dailyWinsStorageKey(record.uid, record.ymd), JSON.stringify(record));
  } catch {
    // Private browsing/storage limits should not break the originating action.
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(DAILY_WINS_EVENT, { detail: record }));
  }
}

/**
 * Record one successful user action. eventId makes service retries idempotent.
 */
export function recordDailyWin({
  uid,
  type,
  eventId,
  patientId,
  minutes = 0,
  ymd = todayYmdInOrgTimeZone(),
}) {
  if (!uid || !type) return null;
  const record = readDailyWins(uid, ymd);
  const stableId = eventId || `${type}:${patientId || ''}:${Date.now()}:${Math.random()}`;
  if (record.eventIds.includes(stableId)) return record;

  const next = {
    ...record,
    eventIds: [...record.eventIds, stableId].slice(-500),
    counters: { ...record.counters },
  };

  if (type === 'threadCleared') {
    if (!patientId || next.clearedPatientIds.includes(patientId)) return record;
    next.clearedPatientIds = [...next.clearedPatientIds, patientId];
    next.counters.threadsCleared = next.clearedPatientIds.length;
  } else if (type === 'patientActivated') {
    if (!patientId || next.activatedPatientIds.includes(patientId)) return record;
    next.activatedPatientIds = [...next.activatedPatientIds, patientId];
    next.counters.patientsActivated = next.activatedPatientIds.length;
  } else if (type === 'careMinutes') {
    next.counters.careMinutes = Math.max(0, next.counters.careMinutes + Number(minutes || 0));
  } else {
    const counterByType = {
      alertResolved: 'alertsResolved',
      messageSent: 'messagesSent',
      inboxZero: 'inboxZeroMoments',
    };
    const key = counterByType[type];
    if (!key) return record;
    next.counters[key] += 1;
  }

  writeDailyWins(next);
  return next;
}

